// ForensicOverlay — usynlig (nesten) vannmerke som legges over sensitivt
// visuelt innhold. Bruker per-visning en unik ID + brukernavn + tidsstempel
// slik at en eventuell lekket screenshot kan spores tilbake til visningen.
//
// Design:
//   - Absolute overlay, `pointer-events: none` (blokkerer ikke interaksjon)
//   - Tekst rotert 30deg, tiled på tvers av bildet
//   - Opacity 0.06 mot mørk bakgrunn (usynlig i normal visning, men fanges
//     opp ved kontrast-økning eller pikselforstørring)
//   - Blanding: mix-blend-mode: difference for å bli synlig ved
//     kompresjon/kontrastjustering
//
// Bruk:
//   <div className="relative">
//     <img src={...} />
//     <ForensicOverlay tag={user.username} viewId={someUUID} />
//   </div>
import React, { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";

export default function ForensicOverlay({ tag, viewId, className = "", testId = "forensic-overlay" }) {
  const { user } = useAuth();
  const label = tag || user?.username || "guest";
  const stamp = useMemo(() => {
    const ts = new Date().toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
    const short = viewId ? String(viewId).slice(0, 8) : Math.random().toString(36).slice(2, 10);
    return `${label} · ${ts} · ${short}`;
  }, [label, viewId]);

  // Bygg en repeterende SVG som CSS background — beste ytelsen for tiled watermark.
  // Ekstremt lav opacitet + soft-light blend gjør at det er tilnærmet usynlig i normal
  // visning, men fanges opp ved kontrast-økning eller pikselforstørring (forensic).
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='420' height='160' viewBox='0 0 420 160'>
    <text x='0' y='80' fill='white' font-family='monospace' font-size='9' opacity='0.035' transform='rotate(-25 210 80)'>${stamp}</text>
  </svg>`;
  const encoded = encodeURIComponent(svg).replace(/'/g, "%27").replace(/"/g, "%22");
  const bg = `url("data:image/svg+xml,${encoded}")`;

  return (
    <div
      aria-hidden="true"
      data-testid={testId}
      className={`pointer-events-none absolute inset-0 z-30 ${className}`}
      style={{
        backgroundImage: bg,
        backgroundRepeat: "repeat",
        mixBlendMode: "soft-light",
        opacity: 0.55,
      }}
    />
  );
}
